import { Box, Divider, Group, lighten, Space, Stack } from "@mantine/core";
import { PlaylistsTable } from "@/components/partners/PlaylistsTable";
import { useStyles } from "@/styles/useStyles";
import { AdvizerSearchInput } from "@/components/advizerSearchControls/AdvizerSearchInput";
import { QuestionSelector } from "@/components/advizerSearchControls/QuestionSelector";
import { useFilteredPlaylists } from "@/store/selectors/playlistSelectors";
import { Text } from "@/components/ui/Text";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import {
  useArrayOfSelectedAdvizerSearchFilters,
  useFilteredAdvizers,
} from "@/store/selectors";
import { ResetAdvizerSearchButton } from "@/components/advizerSearchControls/ResetAdvizerSearchButton";
import { SelectedSearchTags } from "@/components/advizerSearchControls/SelectedSearchTags";
import { AdvizerFilterButton } from "@/components/advizerSearchControls/AdvizerFilterButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "@/store";
import { AdvizerSearchFilters } from "@/components/AdvizerSearchFilters";
import { PlaylistPreview } from "@/components/partners/PlaylistPreview";
import { AssignmentManager } from "@/components/assignments/AssignmentManager";
import { usePartnerUserHasAnyAssignments } from "@/store/selectors/assignmentSelectors";
import { PartnerDashboardLogic } from "@/components/logic/PartnerDashboardLogic";
import BarLoader from "@/components/ui/BarLoader";
import { Helmet } from "react-helmet-async";
import { AssignmentDefaultContent } from "./AssignmentDefaultContent";

export function PartnerDashboard() {
  const { theme, ...styles } = useStyles();
  const { totalFilteredPlaylists } = useFilteredPlaylists();
  const { isAdvizersLoading, isSearchingAdvizers } = useFilteredAdvizers();
  const arrayOfSelectedAdvizerSearchFilters =
    useArrayOfSelectedAdvizerSearchFilters();
  const collapseHeader = useStore(
    (state) => state.advizerSearch.collapseHeader,
  );
  const isInlineAdvizerSearchOpen = useStore(
    (state) => state.advizerSearch.isInlineAdvizerSearchOpen,
  );
  const [hoverResizeHandle, setHoverResizeHandle] = useState(false);
  const isAssignmentManagerOpen = useStore(
    (state) => state.partnerApp.isAssignmentManagerOpen,
  );

  const setCollapseHeader = useStore(
    (state) => state.advizerSearch.setCollapseHeader,
  );
  const setIsInlineAdvizerSearchOpen = useStore(
    (state) => state.advizerSearch.setIsInlineAdvizerSearchOpen,
  );
  const activePlaylistId = useStore(
    (state) => state.partnerApp.activePlaylistId,
  );
  const partnerUserHasAnyAssignments = usePartnerUserHasAnyAssignments();

  const setMostRecentPathname = useStore(
    (state) => state.setMostRecentPathname,
  );
  const leftDrawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMostRecentPathname("/");
  }, [setMostRecentPathname]);

  // scroll to top if playlistId changes
  useEffect(() => {
    if (leftDrawerRef.current) {
      leftDrawerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [activePlaylistId]);

  useEffect(() => {
    if (activePlaylistId) {
      setIsInlineAdvizerSearchOpen(false);
    }
  }, [activePlaylistId, setIsInlineAdvizerSearchOpen]);

  const MainContent = useMemo(() => {
    return (
      <Stack
        // pt={styles.dimensions.headerHeight + 16}
        pt="md"
        pb={0}
        px={0}
        style={{ overflow: "hidden" }}
        h="100%"
        bg="background.1"
        w="100%"
        mx="auto"
        gap={0}
        mah="100vh"
        flex={2}
      >
        <Group px="md" justify="space-between" align="baseline">
          <Group w="100%" flex={1}>
            <AdvizerFilterButton size="sm" buttonText="Filter" />
            <Box flex={1} maw={250} miw={200}>
              <AdvizerSearchInput
                size="sm"
                placeholder="Type to search for playlists"
              />
            </Box>
            <Box flex={1} maw={250} miw={200}>
              <QuestionSelector size="sm" />
            </Box>
            <Group px="xs">
              <Text titleStyle="h6" lh={1.2}>
                Showing {totalFilteredPlaylists} playlists
              </Text>
            </Group>
          </Group>
          {/* {!isAssignmentManagerOpen && (
            <Button
              variant="filled"
              color="primary"
              size="sm"
              radius="xl"
              leftSection={<ResourceConfigs.assignment.Icon size={20} />}
              onClick={() => setIsAssignmentManagerOpen(true)}
            >
              Assignments
            </Button>
          )} */}
        </Group>

        {arrayOfSelectedAdvizerSearchFilters.length > 0 && (
          <Group px="lg" pt="sm">
            <SelectedSearchTags />
            <ResetAdvizerSearchButton />
          </Group>
        )}
        <Space h="md" />
        <Group p={0} w="100%" mah={4}>
          {(isAdvizersLoading || isSearchingAdvizers) && (
            <BarLoader />
            // <BarLoader
            //   width={500}
            //   color={theme.colors.primary[4]}
            //   height={2}
            // />
          )}
        </Group>
        <Divider />
        <Stack style={{ overflow: "auto" }}>
          <PlaylistsTable
            onScroll={(props) => {
              if (
                // props.scrollDirection === "forward" &&
                props.scrollOffset > styles.dimensions.headerHeight
              ) {
                setCollapseHeader(true);
              } else {
                setCollapseHeader(false);
              }
            }}
          />
        </Stack>
      </Stack>
    );
  }, [
    arrayOfSelectedAdvizerSearchFilters.length,
    isAdvizersLoading,
    isSearchingAdvizers,
    setCollapseHeader,
    styles.dimensions.headerHeight,
    totalFilteredPlaylists,
  ]);

  const RightDrawerContent = useMemo(() => {
    if (!isAssignmentManagerOpen) return null;

    if (partnerUserHasAnyAssignments) {
      return <AssignmentManager />;
    }

    return <AssignmentDefaultContent />;
  }, [isAssignmentManagerOpen, partnerUserHasAnyAssignments]);

  const LeftDrawerContent = useMemo(() => {
    if (isInlineAdvizerSearchOpen) {
      return (
        <AdvizerSearchFilters
          hideCompanyFilter
          noTabTitles
          hidePartnerToggle
          hideTabTitle
        />
      );
    }

    if (activePlaylistId) {
      return <PlaylistPreview playlistId={activePlaylistId} />;
    }

    return null;
  }, [activePlaylistId, isInlineAdvizerSearchOpen]);

  return (
    <>
      <Helmet>
        <title>Advize | Partner Dashboard</title>
      </Helmet>
      <Group
        pt={collapseHeader ? 0 : styles.dimensions.headerHeight}
        gap={0}
        align="start"
        h="100vh"
        mah="100vh"
      >
        {LeftDrawerContent ? (
          // <Panel defaultSize={30} minSize={30}>
          <Stack
            ref={leftDrawerRef}
            h={`calc(100vh - ${
              collapseHeader ? 0 : styles.dimensions.headerHeight
            }px)`}
            maw={isInlineAdvizerSearchOpen ? 340 : 450}
            miw={300}
            flex={1}
            pos="sticky"
            top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
            bg="background.0"
            style={{
              zIndex: 10,
              borderRight: `${isInlineAdvizerSearchOpen ? 2 : 4}px solid ${theme.colors.border[3]}`,
              overflow: "auto",
            }}
            gap={0}
            p={0}
          >
            {LeftDrawerContent}
          </Stack>
        ) : // </Panel>
        null}
        <PanelGroup
          direction="horizontal"
          style={{ flex: 2 }}
          // style={{
          //   height: "100vh",
          //   maxHeight: "100vh",
          //   paddingTop: collapseHeader ? 0 : styles.dimensions.headerHeight,
          // }}
        >
          {/* {LeftDrawerContent && <PanelResizeHandle id="left-drawer-resize-handle" />} */}
          <Panel minSize={30}>{MainContent}</Panel>
          {RightDrawerContent && (
            <PanelResizeHandle
              id="right-drawer-resize-handle"
              onMouseEnter={() => setHoverResizeHandle(true)}
              onMouseLeave={() => setHoverResizeHandle(false)}
              style={{
                width: 4,
                position: "relative",
                backgroundColor: hoverResizeHandle
                  ? theme.colors.primary[4]
                  : theme.colors.border[3],
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "col-resize",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "40%",
                  // transform: "translateY(-50%)",
                  right: -5,
                  width: 6,
                  height: "20%",
                  backgroundColor: hoverResizeHandle
                    ? theme.colors.primary[4]
                    : theme.colors.border[3],
                  borderTopRightRadius: 10,
                  borderBottomRightRadius: 10,
                  cursor: "col-resize",
                  zIndex: 100,
                  // opacity: hoverResizeHandle ? 1 : 0,
                }}
              />
            </PanelResizeHandle>
          )}
          {RightDrawerContent ? (
            <Panel defaultSize={40} minSize={20}>
              <Stack
                h={`calc(100vh - ${collapseHeader ? 0 : 0}px)`}
                justify="flex-start"
                align="flex-start"
                // maw={450}
                bg={lighten(theme.colors.background[1], 0.3)}
                // flex={1}
                pos="sticky"
                // top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
                top={collapseHeader ? 0 : 0}
                pb="lg"
                style={{
                  zIndex: 10,
                  overflow: "auto",
                  // borderLeft: `4px solid ${theme.colors.border[3]}`,
                  // boxShadow: `-4px 0 8px -2px rgba(0, 0, 0, 0.15)`,
                }}
                gap={0}
                p={0}
              >
                {RightDrawerContent}
              </Stack>
            </Panel>
          ) : null}
        </PanelGroup>
      </Group>
      <PartnerDashboardLogic />
    </>
  );
}

// function AssignmentDefaultContent() {
//   const { theme } = useStyles();
//   const isLoggedIn = useIsLoggedIn();

//   // const isLoggedIn = useIsLoggedIn();
//   const setIsAssignmentManagerOpen = useStore(
//     (state) => state.partnerApp.setIsAssignmentManagerOpen,
//   );
//   const setActiveAssignmentId = useStore(
//     (state) => state.partnerApp.setActiveAssignmentId,
//   );

//   const defaultAssignmentPromptConfigs = useDefaultAssignmentPromptConfigs();
//   return (
//     <Stack align="center" justify="center" px={0} gap="lg">
//       <Group justify="flex-end" w="100%" px="sm" py="sm">
//         <ActionIcon
//           variant="light"
//           size="md"
//           radius="xl"
//           color="text.6"
//           style={{ zIndex: 100 }}
//           onClick={() => setIsAssignmentManagerOpen(false)}
//         >
//           <IconX size={20} />
//         </ActionIcon>
//       </Group>
//       <Stack align="center" justify="center" px="xl" gap="lg">
//         <ResourceConfigs.assignment.Icon
//           size={80}
//           color={theme.colors.primary[7]}
//         />
//         <Text titleStyle="h2" lh={1.2} ta="center">
//           Design career exploration assignments and get real-time results
//         </Text>
//         {/* <Space h="xs" /> */}
//         <Button
//           variant="filled"
//           radius="xl"
//           size="md"
//           color="primary"
//           leftSection={<IconPlus />}
//           onClick={async () => {
//             if (!isLoggedIn) {
//               Navigation.navToLogin();
//               return;
//             }
//             const assignmentId =
//               await AssignmentService.createNewBlankAssignment({
//                 defaultAssignmentPromptConfigs,
//               });

//             setActiveAssignmentId(assignmentId);
//           }}
//         >
//           Create an assignment
//         </Button>
//         <Space h="xs" />
//         <Link
//           to={config.assignmentGuideUrl}
//           style={{
//             fontSize: 12,
//             display: "flex",
//             alignItems: "center",
//             opacity: 0.75,
//           }}
//           target="_blank"
//         >
//           Read Assignment Guide + FAQ{" "}
//           <IconExternalLink size={16} style={{ marginLeft: 4 }} />
//         </Link>

//         <Space h="xs" />

//         {/* <Text size="sm" ta="center">
//           <Link
//             to="/partner/assignments"
//             style={{ display: "flex", alignItems: "center", gap: 4 }}
//           >
//             Click here to check out an example assignment
//           </Link>
//         </Text> */}
//       </Stack>
//     </Stack>
//   );
// }
